import {
  Menu, MenuButton, MenuItem, MenuItems,
  Transition,
} from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, EyeIcon, EyeSlashIcon, SparklesIcon, UserIcon } from '@heroicons/react/24/solid';
import { Tooltip } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { Company } from '../../../../@types/Company';
import { PaymentStatus } from '../../../../@types/PaymentStatus';
import { refreshCompanyData, setCompanyNameHidden, setSelectedCompany } from '../../../../store/AuthReducer';
import { useAppDispatch } from '../../../../store/hook';
import type { RootState } from '../../../../store/store';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup/Popup.module';
import SubscriptionPanel from '../../../Subscription/SubscriptionPanel.module';
import CompanyDropdown from '../CompanyDropdown/CompanyDropdown.module';

// A user holds several fonctions per company (e.g. legal representative + virtual RGPD referent),
// so the same company can come several times: display it once.
const uniqueCompanies = (list: Company[] | null): Company[] => [
  ...new Map((list ?? []).map((item) => [item.id, item])).values(),
];

const UserMenu: React.FC<{ hideOnMobile?: boolean }> = ({ hideOnMobile = true }) => {
  const dispatch = useAppDispatch();
  const authData = useSelector((state: RootState) => state.auth);
  const {
    user,
    company,
    token,
    availableCompanies,
    companyNameHidden,
  } = authData;
  const [companies, setCompanies] = useState(availableCompanies);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  const fetchCompanies = async() => {
    try {
      const response = await fetch(`/api/companies?step=1&limit=10${searchQuery ? `&filterSearch=${searchQuery}` : ''}`, JWTHeader(token));
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.results);
      } else {
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
      }
    } catch (error) {
      console.error('Error fetching companies :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  useEffect(() => {
    if (user && user.roles.includes('ROLE_ADMIN')) {
      fetchCompanies();
    } else {
      setCompanies(availableCompanies);
    }
  }, [user, searchQuery]);

  const handleCompanyChange = (selectedCompany: Company) => {
    if (selectedCompany) {
      dispatch(setSelectedCompany(selectedCompany));
    }
  };

  const getUserIconColor = () => {
    if (!user) {return 'text-rgpd-blue-dark';}
    if (user.roles.includes('ROLE_DEVELOPER')) {return 'text-red-600';}
    if (user.roles.includes('ROLE_ADMIN')) {return 'text-green-600';}
    return 'text-rgpd-blue-dark';
  };

  const activateDemoMode = () => {
    if (!company) {
      showToast('error', 'Aucune entreprise sélectionnée.');
      return;
    }
    
    try {
      // Create a copy of the company object
      const updatedCompany = { ...company };
      
      // Set one year from now date
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      const oneYearFromNowISO = oneYearFromNow.toISOString();
      
      // Set ProAbono subscription status (all modules accessible with active subscription)
      updatedCompany.paymentStatus = PaymentStatus.ACTIVE;
      updatedCompany.proAbonoSubscriptionEnd = oneYearFromNowISO;
      updatedCompany.lastPaymentDate = new Date().toISOString();
      
      // Update the company in the Redux store
      dispatch(refreshCompanyData(updatedCompany));
      
      // Format date for user-friendly display
      const formattedDateString = oneYearFromNow.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      
      showToast('success', `Mode démo activé pour tous les modules jusqu'au ${formattedDateString}.`);
    } catch (error) {
      console.error('Error activating demo mode:', error);
      showToast('error', 'Une erreur est survenue lors de l\'activation du mode démo.');
    }
  };


  return (
    <Menu as="div" className="relative">
      <div className="flex items-center gap-x-2">
        <h1 className={`text-sm font-bold text-black 2xl:text-lg ${hideOnMobile ? 'hidden md:flex' : 'flex'} items-center gap-x-2`}>
          {user && user.roles.includes('ROLE_ADMIN') && company && company.id && (
            <Tooltip content="Ouvrir la fiche entreprise" className="tooltip tooltip-bottom">
              <Link to={ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', company.id.toString())}>
                <ArrowTopRightOnSquareIcon className="inline h-5 w-5" />
              </Link>
            </Tooltip>
          )}
          {companies && company && company.name ? (
            <div className="flex items-center">
              <CompanyDropdown
                dropdownActivated={uniqueCompanies(availableCompanies).length > 1 || (user && user.roles.includes('ROLE_ADMIN'))}
                options={uniqueCompanies(companies).map((item) => (item.id === company.id && companyNameHidden ? { ...item, name: 'DEMO CROISSANCE' } : item))}
                selectedOption={companyNameHidden ? { ...company, name: 'DEMO CROISSANCE' } : company}
                onOptionSelect={handleCompanyChange}
                setSearchQuery={setSearchQuery}
              />
            </div>
          ) : (
            <Skeleton height={32} width={130} />
          )}
        </h1>
        
        {/* Demo Mode Button */}
        {user?.roles.includes('ROLE_ADMIN') && (
          <Tooltip content="Activer le mode démo ADMIN" className="tooltip tooltip-bottom">
            <button
              onClick={activateDemoMode}
              className="rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-yellow-500"
              aria-label="Activer le mode démo"
            >
              <SparklesIcon className="h-4 w-4" />
            </button>
          </Tooltip>
        )}

        {user?.roles.includes('ROLE_ADMIN') && (
          <Tooltip
            content={
              companyNameHidden
                ? 'Afficher à nouveau le nom de l\'entreprise'
                : 'Masquer le nom de l\'entreprise pour le mode démo'
            }
            className="tooltip tooltip-bottom"
          >
            <button
              onClick={() => dispatch(setCompanyNameHidden(!companyNameHidden))}
              className={`rounded-full p-1.5 transition-colors ${companyNameHidden ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
              aria-label={companyNameHidden ? 'Afficher le nom de l’entreprise' : 'Masquer le nom de l’entreprise'}
            >
              {companyNameHidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </Tooltip>
        )}
        
        <MenuButton className="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
          <UserIcon 
            className={`h-4 w-4 ${getUserIconColor()}`} 
            aria-hidden="true" 
          />
        </MenuButton>
      </div>

      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-[1000] mt-2 w-64 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          <MenuItem>
            {() => (
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="mb-1 text-sm font-medium text-gray-900">
                  {user?.firstname} {user?.lastname}
                </div>
                <div className="text-xs font-semibold text-gray-400 hover:text-rgpd-blue">
                  {user?.email}
                </div>
              </div>
            )}
          </MenuItem>

          <div className="mx-3">
            <MenuItem>
              {({ active }) => (
                <Link
                  to={ROUTES.EXTERNAL_MY_INFORMATION}
                  className={`${
                    active ? 'bg-blue-50' : ''
                  } block rounded-md px-4 py-2 text-sm font-semibold text-gray-500 hover:bg-blue-50/50 hover:text-rgpd-blue`}
                >
                  Mes informations
                </Link>
              )}
            </MenuItem>

            <MenuItem>
              {({ active }) => (
                <Link
                  to={ROUTES.PAYMENT_INVOICE}
                  className={`${
                    active ? 'bg-blue-50' : ''
                  } mt-1 block rounded-md px-4 py-2 text-sm font-semibold text-gray-500 hover:bg-blue-50/50 hover:text-rgpd-blue`}
                >
                  Mes factures
                </Link>
              )}
            </MenuItem>

            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => setIsSubscriptionModalOpen(true)}
                  className={`${
                    active ? 'bg-blue-50' : ''
                  } mt-1 block w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-gray-500 hover:bg-blue-50/50 hover:text-rgpd-blue`}
                >
                  Mon abonnement
                </button>
              )}
            </MenuItem>

            <MenuItem>
              {({ active }) => (
                <Link
                  to={ROUTES.LOGOUT}
                  className={`${
                    active ? 'bg-blue-50' : ''
                  } mt-1 block rounded-md px-4 py-2 text-sm font-semibold text-gray-500 hover:bg-blue-50/50 hover:text-rgpd-blue`}
                >
                  Déconnexion
                </Link>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>

      <Popup
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        width="90vw"
        height="90vh"
        maxWidth="800px"
        title="Mon abonnement"
      >
        <div className="flex h-full flex-col overflow-y-auto">
          <SubscriptionPanel onClose={() => setIsSubscriptionModalOpen(false)} />
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 px-6 pt-4 pb-4">
            <Link
              to={ROUTES.EXTERNAL_CGU}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-500 hover:text-blue-700"
            >
              CGU et Mentions Légales
            </Link>
            <button
              onClick={() => setIsSubscriptionModalOpen(false)}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Fermer
            </button>
          </div>
        </div>
      </Popup>
    </Menu>
  );
};

export default UserMenu;
